import { useEffect, useRef, useState } from "react";
import { Mic, PlayCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import AgentAvatar from "@/components/app/AgentAvatar";
import AssistantFace from "@/components/AssistantFace";
import { ButtonLink, Frame } from "./primitives";
import AppPreview from "./AppPreview";

const TABS = ["Clients", "Workouts", "Nutrition", "AI Agent"];
const TAB_LABEL = { "AI Agent": "AI Assistant" };

// What the assistant "says" in its bubble, one after another.
const LINES = [
  "What can I take off your plate today?",
  "Sarah's plan for next week is ready.",
  "3 clients checked in. Want the summary?",
  "Meal plan for Arman: 2,200 kcal, no dairy. Done.",
  "Reminder set: call Daniel on Friday.",
  "Quick break. Arm day!",
];
const WORKOUT_LINE = LINES.length - 1;

// What a coach might type, cycling in the prompt bar.
const PROMPTS = ["Plan next week for Sarah…", "Make Arman a 2,200 kcal meal plan…", "Who hasn't checked in this week?", "Send Daniel his chest videos…"];

function useMedia(query) {
  const [on, setOn] = useState(() => typeof window !== "undefined" && window.matchMedia?.(query).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return undefined;
    const f = () => setOn(mq.matches);
    mq.addEventListener?.("change", f);
    return () => mq.removeEventListener?.("change", f);
  }, [query]);
  return on;
}

/** Types text in, character by character. Calls onTyping(true/false) so the character can move its mouth. */
function useTypewriter(items, { typeMs = 38, holdMs = 2600, onTyping } = {}) {
  const [i, setI] = useState(0);
  const [n, setN] = useState(0);
  const reduce = useMedia("(prefers-reduced-motion: reduce)");
  const text = items[i % items.length];
  const hold = typeof holdMs === "function" ? holdMs(i % items.length) : holdMs;
  useEffect(() => {
    if (reduce) {
      setN(text.length);
      const t = setTimeout(() => setI((x) => x + 1), hold + 1500);
      return () => clearTimeout(t);
    }
    if (n < text.length) {
      onTyping?.(true);
      const t = setTimeout(() => setN((x) => x + 1), typeMs);
      return () => clearTimeout(t);
    }
    onTyping?.(false);
    const t = setTimeout(() => {
      setN(0);
      setI((x) => x + 1);
    }, hold);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n, text, reduce]);
  return { shown: text.slice(0, n), full: text, typing: n < text.length, index: i % items.length };
}

function AssistantStage() {
  const wide = useMedia("(min-width: 1024px)");
  // On the homepage the mouth stays still: only the bubble "talks".
  const levelsRef = useRef({ a: 0, u: 0 });

  const bubble = useTypewriter(LINES, { typeMs: 34, holdMs: (k) => (k === WORKOUT_LINE ? 7200 : 3200) });
  // After "Quick break", it grabs the dumbbells for a few reps.
  const [workoutKey, setWorkoutKey] = useState(0);
  const onBreak = bubble.index === WORKOUT_LINE && !bubble.typing;
  useEffect(() => {
    if (onBreak) setWorkoutKey((k) => k + 1);
  }, [onBreak]);
  const prompt = useTypewriter(PROMPTS, { typeMs: 55, holdMs: 1800 });

  return (
    <div className="relative mx-auto flex w-full max-w-[560px] flex-col items-center">
      <div className="relative flex w-full flex-col items-center">
        {/* speech bubble: sits above its head, never over the headband */}
        <div
          className="relative z-10 w-[min(270px,80%)] self-end rounded-2xl border border-line bg-white px-4 py-3 text-left shadow-[0_12px_40px_-18px_rgba(0,0,0,0.3)] sm:mr-4"
          aria-live="polite"
        >
          <p className="text-[12px] font-medium text-neutral-500">Your assistant</p>
          <p className="mt-1 min-h-[2.6em] text-[15px] font-medium leading-snug text-ink sm:text-[17px]">
            {bubble.shown}
            {bubble.typing && <span className="ml-0.5 inline-block h-[1em] w-[2px] translate-y-[2px] animate-pulse bg-ink" aria-hidden="true" />}
          </p>
          <span className="absolute -bottom-[7px] left-6 h-3.5 w-3.5 rotate-45 border-b border-r border-line bg-white" aria-hidden="true" />
        </div>
        {/* the empty space above its head tucks under the bubble */}
        <div className="relative" style={{ marginTop: wide ? -62 : -38 }}>
          <AgentAvatar phase="idle" levelsRef={levelsRef} size={wide ? 400 : 250} workoutKey={workoutKey} autoWorkout={false} />
        </div>
      </div>

      {/* prompt bar */}
      <a
        href="/signup"
       
        className="group -mt-2 flex w-full items-center gap-3 rounded-full border border-line bg-white py-2 pl-5 pr-2 shadow-[0_14px_40px_-20px_rgba(0,0,0,0.35)] transition-shadow hover:shadow-[0_18px_48px_-20px_rgba(0,0,0,0.45)]"
        aria-label="Try the assistant: start for free"
      >
        <AssistantFace className="h-8 w-8 -ml-2" />
        <span className="min-w-0 flex-1 truncate text-left text-[15px] text-neutral-400 sm:text-base">
          {prompt.shown}
          <span className="ml-0.5 inline-block h-[1.05em] w-[1.5px] translate-y-[3px] animate-pulse bg-neutral-400" aria-hidden="true" />
        </span>
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-lemon transition-transform group-hover:scale-105 sm:h-14 sm:w-14">
          <Mic className="h-5 w-5 text-black" strokeWidth={2.2} />
        </span>
      </a>
    </div>
  );
}

export default function Hero() {
  const [tab, setTab] = useState("AI Agent");

  return (
    <section>
      <Frame>
        <div className="grid items-center gap-12 px-4 pb-14 pt-12 sm:px-10 sm:pb-20 sm:pt-16 lg:grid-cols-[minmax(0,1.12fr)_minmax(0,0.88fr)] lg:gap-4 lg:pt-20">
          <div className="flex flex-col items-start">
            <h1 className="text-[42px] font-medium leading-[1.08] tracking-[-0.03em] sm:text-[58px] lg:text-[52px] xl:text-[62px]">
              <span className="sm:whitespace-nowrap">Your own AI assistant.</span>
              <br />
              <span className="sm:whitespace-nowrap">Built for coaching.</span>
            </h1>
            <p className="mt-6 max-w-[560px] text-pretty text-lg leading-8 text-[#232529] sm:text-[21px] sm:leading-[34px]">
              Manage clients, create plans and handle follow-ups with an assistant that works alongside you.
            </p>
            <div className="mt-8 flex w-full flex-wrap gap-3 [&>a]:flex-1 sm:w-auto sm:[&>a]:flex-none">
              <ButtonLink href="/signup" className="h-[50px] px-7 text-[16px]">
                Start for free
              </ButtonLink>
              <ButtonLink variant="light" href="#agent" className="h-[50px] px-6 text-[16px]">
                <PlayCircle className="h-5 w-5" strokeWidth={1.8} />
                See how it works
              </ButtonLink>
            </div>
            <p className="mt-6 text-[15px] text-neutral-500">
              You coach. Your assistant takes care of the details.
            </p>
          </div>

          <AssistantStage />
        </div>

        <div role="tablist" aria-label="Product tour" className="grid grid-cols-4 border-y border-line">
          {TABS.map((t, i) => {
            const active = t === tab;
            return (
              <button
                key={t}
                role="tab"
                type="button"
                aria-selected={active}
                onClick={() => setTab(t)}
                className={cn(
                  "flex h-[52px] items-center justify-center gap-1.5 text-[13px] font-medium transition-colors sm:text-[15px]",
                  i > 0 && "border-l border-line",
                  active ? "bg-soft text-ink shadow-[inset_0_-2px_0_#171717]" : "text-neutral-400 hover:text-neutral-600",
                )}
              >
                {TAB_LABEL[t] || t}
              </button>
            );
          })}
        </div>

        <AppPreview tab={tab} />
      </Frame>
    </section>
  );
}
